import { observer } from 'mobx-react-lite'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { ContentLoading } from '@/components/ContentLoading'
import { ContentError } from '@/components/ContentError'
import {
  JobScopeChangeRequestsCard,
  ScopeChangeRequest,
} from '@/features/jobs/details/components/JobScopeChangeRequestsCard'

export interface JobScopeChangeRequestsCardContainerProps {
  vm: JobDetailsViewModel
  /**
   * Leaves out the pending request, which the job's banner already shows.
   */
  resolvedOnly?: boolean
}

export const JobScopeChangeRequestsCardContainer = observer(
  function JobScopeChangeRequestsCardContainer({
    vm,
    resolvedOnly,
  }: JobScopeChangeRequestsCardContainerProps) {
    const scopeChangeRequests = resolvedOnly
      ? vm.scopeChangesVm.resolvedScopeChangeRequestsNewestFirst
      : vm.scopeChangesVm.scopeChangeRequestsNewestFirst

    const renderRequests = () =>
      scopeChangeRequests.map((scopeChangeRequest) => (
        <ScopeChangeRequest
          key={scopeChangeRequest.id}
          status={scopeChangeRequest.status}
          isPending={scopeChangeRequest.isPending}
          isAccepted={scopeChangeRequest.isAccepted}
          isRejected={scopeChangeRequest.isRejected}
          isApplyFailed={scopeChangeRequest.isApplyFailed}
          isCreatedByAdmin={scopeChangeRequest.isCreatedByAdmin}
          createTime={scopeChangeRequest.createTime}
          latestResponseTime={
            scopeChangeRequest.latestResponseTime ?? undefined
          }
          onView={() =>
            vm.scopeChangesVm.viewScopeChangeRequests(scopeChangeRequest)
          }
        />
      ))

    return (
      <JobScopeChangeRequestsCard
        count={scopeChangeRequests.length}
        showActions={vm.scopeChangesVm.canChangeScope}
        showUpdateJobScopeOption={vm.scopeChangesVm.canUpdateJobScope}
        onUpdateJobScope={vm.scopeChangesVm.updateJobScopeVm.show}
        onRequestScopeChanges={vm.scopeChangesVm.requestScopeChangesVm.show}
      >
        {vm.scopeChangesVm.fetch.match({
          pending: () =>
            vm.scopeChangesVm.isRevalidating ? (
              renderRequests()
            ) : (
              <ContentLoading size="section" />
            ),
          rejected: (err) => (
            <ContentError
              size="section"
              title="An error occurred while loading scope change requests."
              error={err}
            />
          ),
          resolved: renderRequests,
        })}
      </JobScopeChangeRequestsCard>
    )
  },
)
